<template>
  <div class="flex flex-col items-center min-h-screen bg-slate-100 dark:bg-slate-900 p-4 sm:p-6">
    <div
      class="w-full max-w-5xl bg-white dark:bg-slate-800/50 dark:backdrop-blur-sm shadow-2xl rounded-2xl p-6 sm:p-10 text-center ring-1 ring-slate-900/5 dark:ring-white/10"
    >
      <div class="mb-8 flex flex-col items-center animate-fade-in-down">
        <div class="relative mb-4">
          <span class="material-symbols-outlined text-6xl sm:text-7xl text-purple-600 dark:text-purple-400">
            admin_panel_settings
          </span>
          <div class="absolute -inset-2 rounded-full bg-purple-500/10 blur-xl dark:bg-purple-400/10"></div>
        </div>
        <h2 class="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100">
          Bem-vindo(a), <span class="text-purple-600 dark:text-purple-400">{{ username || 'Admin' }}</span>!
        </h2>
      </div>

      <p class="text-base sm:text-lg text-slate-600 dark:text-slate-300 mb-12 max-w-2xl mx-auto animate-fade-in-down" style="animation-delay: 0.2s;">
        Este é o seu painel de controle central. Gerencie o sistema com eficiência e tenha acesso rápido às principais funcionalidades.
      </p>

      <div class="mb-16 overflow-hidden rounded-xl shadow-lg group animate-fade-in-up" style="animation-delay: 0.4s;">
        <div class="aspect-video bg-gradient-to-tr from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700">
          <model-viewer
            src="/grafico-3d.glb"
            alt="Gráfico 3D interativo - ControlEase"
            auto-rotate
            camera-controls
            shadow-intensity="1.2"
            style="width: 100%; height: 100%; --model-viewer-background-color: transparent;"
          >
            <template #progress-bar>
              <div class="progress-bar hide">
                <div class="update-bar"></div>
              </div>
            </template>
          </model-viewer>
        </div>
      </div>
      
      <div class="animate-fade-in-up" style="animation-delay: 0.6s;">
        <h3 class="text-xl sm:text-2xl font-semibold text-slate-700 dark:text-slate-200 mb-6">Ações Rápidas</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            
          <a href="/admin/users" class="group flex flex-col items-center text-center p-6 bg-slate-50 dark:bg-slate-800 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700/50 hover:ring-purple-500 dark:hover:ring-purple-400 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl">
            <div class="mb-4 rounded-full bg-purple-100 dark:bg-purple-900/50 p-3 ring-8 ring-purple-50 dark:ring-purple-900/30 group-hover:scale-110 transition-transform duration-300">
              <span class="material-symbols-outlined text-3xl text-purple-600 dark:text-purple-300">group</span>
            </div>
            <h4 class="font-bold text-lg text-slate-800 dark:text-slate-100 mb-1">Gerenciar Usuários</h4>
            <p class="text-sm text-slate-500 dark:text-slate-400">Adicione, edite ou remova contas.</p>
          </a>
            
          <a href="/admin/reports" class="group flex flex-col items-center text-center p-6 bg-slate-50 dark:bg-slate-800 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700/50 hover:ring-purple-500 dark:hover:ring-purple-400 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl">
            <div class="mb-4 rounded-full bg-purple-100 dark:bg-purple-900/50 p-3 ring-8 ring-purple-50 dark:ring-purple-900/30 group-hover:scale-110 transition-transform duration-300">
              <span class="material-symbols-outlined text-3xl text-purple-600 dark:text-purple-300">analytics</span>
            </div>
            <h4 class="font-bold text-lg text-slate-800 dark:text-slate-100 mb-1">Ver Relatórios</h4>
            <p class="text-sm text-slate-500 dark:text-slate-400">Acesse dados e métricas de uso.</p>
          </a>

          <a href="/admin/settings" class="group flex flex-col items-center text-center p-6 bg-slate-50 dark:bg-slate-800 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700/50 hover:ring-purple-500 dark:hover:ring-purple-400 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl md:col-span-2 lg:col-span-1">
            <div class="mb-4 rounded-full bg-purple-100 dark:bg-purple-900/50 p-3 ring-8 ring-purple-50 dark:ring-purple-900/30 group-hover:scale-110 transition-transform duration-300">
              <span class="material-symbols-outlined text-3xl text-purple-600 dark:text-purple-300">settings</span>
            </div>
            <h4 class="font-bold text-lg text-slate-800 dark:text-slate-100 mb-1">Configurações</h4>
            <p class="text-sm text-slate-500 dark:text-slate-400">Ajuste os parâmetros do sistema.</p>
          </a>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import AuthService from '@/service/authService';

const username = ref('');

const fetchUsername = async () => {
  const currentUser = AuthService.getCurrentUser();
  const fallbackUsername = currentUser?.username || 'Admin';

  if (!currentUser?.usuario_id || !currentUser?.token) {
    username.value = fallbackUsername;
    return;
  }

  try {
    const response = await axios.get(
      `https://backend.controlease.com.br/api/users/${currentUser.usuario_id}`,
      { headers: { Authorization: `Bearer ${currentUser.token}` } }
    );
    username.value = response.data?.username || fallbackUsername;
  } catch (error) {
    console.error('Erro ao buscar o nome do usuário:', error);
    username.value = fallbackUsername;
  }
};

onMounted(fetchUsername);
</script>

<style scoped>

.progress-bar {
  display: block; width: 33%; height: 10%; max-height: 2%; position: absolute;
  left: 50%; top: 50%; transform: translate3d(-50%, -50%, 0);
  border-radius: 25px; box-shadow: 0px 3px 10px 3px rgba(0, 0, 0, 0.2);
  background-color: rgba(200, 200, 200, 0.5); border: 1px solid rgba(255, 255, 255, 0.9);
}

.update-bar {
  background-color: rgba(255, 255, 255, 0.9); width: 0%; height: 100%;
  border-radius: 25px; float: left; transition: width 0.3s;
}

.progress-bar.hide { visibility: hidden; transition: visibility 0.3s; }

@keyframes fade-in-down {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-down { animation: fade-in-down 0.6s ease-in-out both; }
.animate-fade-in-up { animation: fade-in-up 0.6s ease-in-out both; }
</style>